/**
 * PAN Verification Page — Income Tax Department (FREE) · God Mode
 * ================================================================
 * Rich result rendering: format_validation, aadhaar_linkage, deceased_check,
 * risk_assessment with FORMAT_WARNING colour, next-step actions for Company
 * PAN ("Run MCA21 Lookup") and Individual PAN (auto-OpenSanctions trigger).
 * No competitor names. AML-integrated CTA buttons.
 */
import React, { useState, useEffect, useCallback, useRef } from "react";
import axios from "axios";
import { useNavigate } from "react-router-dom";
import {
  IdCard, Search, Loader2, ShieldCheck, AlertTriangle,
  CheckCircle2, XCircle, Sparkles, History, Save, X, Building2,
  ArrowRight, ShieldAlert, Info, Award,
} from "lucide-react";
import { toast } from "sonner";
import IdentityResultCard from "../components/IdentityResultCard";
import { fromPanVerify } from "../lib/identityNormalizers";

const API = `${window.location.origin}/api/pan-verify`;
const SANCTIONS_API = `${window.location.origin}/api/opensanctions`;

const RISK_COLOR = {
  LOW: "#22c55e",
  MEDIUM: "#f59e0b",
  HIGH: "#ef4444",
  CRITICAL: "#dc2626",
  FORMAT_WARNING: "#f59e0b",
};
const STATUS_COLOR = {
  VALID: "#22c55e",
  INVALID: "#dc2626",
  DEACTIVATED: "#f97316",
  NOT_FOUND: "#94a3b8",
  FAKE: "#dc2626",
  FORMAT_ERROR: "#f59e0b",
};
const DISPLAY_COLOR_MAP = { green: "#22c55e", red: "#dc2626", grey: "#94a3b8", orange: "#f59e0b" };

const inputStyle = { width: "100%", background: "#080c12", border: "1px solid #1e2530", borderRadius: 6, padding: "10px 14px", color: "#f1f5f9", fontSize: 13, outline: "none", fontFamily: "ui-monospace, monospace", letterSpacing: 1 };
const btnPrimary = { background: "linear-gradient(135deg, #2563eb, #1d4ed8)", border: "none", color: "white", padding: "10px 18px", borderRadius: 6, fontSize: 13, fontWeight: 700, cursor: "pointer", display: "inline-flex", alignItems: "center", gap: 8 };
const btnGhost = { background: "transparent", border: "1px solid #1e2530", borderRadius: 6, color: "#94a3b8", padding: "8px 14px", fontSize: 12, fontWeight: 600, cursor: "pointer", display: "inline-flex", alignItems: "center", gap: 6 };
const btnAction = { background: "linear-gradient(135deg, #7c3aed, #5b21b6)", border: "none", color: "white", padding: "10px 16px", borderRadius: 6, fontSize: 12, fontWeight: 700, cursor: "pointer", display: "inline-flex", alignItems: "center", gap: 8 };

function KV({ k, v, mono, color, sub }) {
  return (
    <div>
      <div style={{ color: "#64748b", fontSize: 10, fontWeight: 700, textTransform: "uppercase", letterSpacing: 0.6, marginBottom: 4 }}>{k}</div>
      <div style={{ color: color || "#f1f5f9", fontSize: 13, fontWeight: 600, fontFamily: mono ? "ui-monospace, monospace" : "inherit" }}>{v || "—"}</div>
      {sub && <div style={{ color: "#64748b", fontSize: 10, marginTop: 2 }}>{sub}</div>}
    </div>
  );
}

export default function PANVerifyPage() {
  const [pan, setPan] = useState("");
  const [expectedType, setExpectedType] = useState("");
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);
  const [sanctionsLoading, setSanctionsLoading] = useState(false);
  const [sanctionsResult, setSanctionsResult] = useState(null);
  const navigate = useNavigate();
  const autoTriggeredRef = useRef(null);

  const loadRecent = useCallback(async () => {
    // SESSION-ONLY mode: no recent lookups persisted.
  }, []);

  useEffect(() => { loadRecent(); }, [loadRecent]);

  const runSanctions = useCallback(async (name) => {
    if (!name || sanctionsLoading) return;
    setSanctionsLoading(true);
    setSanctionsResult(null);
    try {
      const { data } = await axios.post(`${SANCTIONS_API}/lookup`, { query_name: name }, { withCredentials: true });
      setSanctionsResult({ result: data.result, risk: data.risk });
      const matches = data.result?.total_matches || 0;
      if (matches > 0) {
        toast.error(`Sanctions hit: ${matches} match(es) — ${(data.risk?.severity_level || "").toUpperCase()}`);
      } else {
        toast.success("Sanctions: CLEAR — no matches");
      }
    } catch (e) {
      toast.error("Auto sanctions check failed");
    } finally {
      setSanctionsLoading(false);
    }
  }, [sanctionsLoading]);

  // Auto-trigger OpenSanctions for VALID Individual PAN with a usable name
  useEffect(() => {
    if (!result) return;
    const autoFlag = result.auto_trigger_sanctions;
    const name = result.name_on_pan || result.verification_result?.name_on_pan;
    const panType = (result.pan_type || "").toLowerCase();
    const key = `${result.pan_number}-${name || ""}`;
    if (autoFlag && name && panType === "individual" && autoTriggeredRef.current !== key) {
      autoTriggeredRef.current = key;
      runSanctions(name);
    }
  }, [result, runSanctions]);

  const submit = async () => {
    const v = pan.trim().toUpperCase();
    if (v.length !== 10) { toast.error("PAN must be 10 characters"); return; }
    setLoading(true); setResult(null); setSanctionsResult(null);
    autoTriggeredRef.current = null;
    try {
      const { data } = await axios.post(`${API}/lookup`, { pan: v, expected_type: expectedType || undefined }, { withCredentials: true });
      setResult(data.result);
    } catch (e) { toast.error(e.response?.data?.detail || "Verification failed"); }
    finally { setLoading(false); }
  };

  const riskLevel = result?.risk_assessment?.overall_risk || result?.risk_level || "LOW";
  const riskColor = RISK_COLOR[riskLevel] || "#94a3b8";
  const status = result?.verification_result?.status || result?.status;
  const statusColor = STATUS_COLOR[status] || "#94a3b8";
  const panType = result?.verification_result?.pan_type || result?.pan_type;
  const isCompany = (panType || "").toLowerCase() === "company";
  const isFormatError = status === "FORMAT_ERROR";

  // Aadhaar / Deceased blocks
  const aadhaar = result?.aadhaar_linkage;
  const deceased = result?.deceased_check;
  const aadhaarValue = aadhaar?.display_value || (result?.aadhaar_linked == null ? "N/A" : (result.aadhaar_linked ? "Yes" : "No"));
  const aadhaarColor = aadhaar?.display_color ? DISPLAY_COLOR_MAP[aadhaar.display_color] : (result?.aadhaar_linked ? "#22c55e" : "#94a3b8");
  const deceasedValue = deceased?.display_value || (result?.is_deceased == null ? "N/A" : (result.is_deceased ? "Yes" : "No"));
  const deceasedColor = deceased?.display_color ? DISPLAY_COLOR_MAP[deceased.display_color] : (result?.is_deceased ? "#dc2626" : "#22c55e");

  const formatGuidance = result?.risk_assessment?.format_error_guidance;
  const nextSteps = result?.next_steps || {};
  const stepsToShow = isFormatError
    ? (nextSteps.for_invalid_pan || [])
    : isCompany
      ? (nextSteps.for_company_pan || [])
      : (nextSteps.for_individual_pan || []);

  return (
    <div data-testid="pan-verify-page" style={{ color: "#f1f5f9" }}>
      <div className="flex items-center justify-between mb-2">
        <div className="flex items-center gap-3">
          <IdCard style={{ width: 26, height: 26, color: "#2563eb" }} />
          <h1 style={{ fontSize: 26, fontWeight: 700, margin: 0 }} data-testid="page-title">PAN Verification</h1>
          <span style={{ background: "rgba(34,197,94,0.15)", color: "#22c55e", border: "1px solid #22c55e55", fontSize: 10, padding: "3px 8px", borderRadius: 4, fontWeight: 700, textTransform: "uppercase", letterSpacing: 0.5 }}>FREE · Income Tax Dept.</span>
          <span style={{ background: "rgba(148,163,184,0.12)", color: "#cbd5e1", border: "1px solid #94a3b855", fontSize: 10, padding: "3px 8px", borderRadius: 4, fontWeight: 700, textTransform: "uppercase", letterSpacing: 0.5 }} data-testid="session-only-badge">Session-only · Not stored</span>
        </div>
      </div>
      <p style={{ color: "#94a3b8", fontSize: 13, marginBottom: 18 }}>
        AML-integrated PAN verification via Income Tax Department. Validates format, decodes structure,
        returns Aadhaar linkage, deceased flag, and auto-triggers Rudrik Sanctions & MCA21 lookups.
        Results are <strong style={{ color: "#cbd5e1" }}>session-only</strong> — nothing is written to the database.
      </p>

      {/* Input */}
      <div style={{ background: "#0d1117", border: "1px solid #1e2530", borderRadius: 10, padding: 18, marginBottom: 16 }}>
        <div style={{ display: "grid", gridTemplateColumns: "2fr 1fr auto", gap: 12, alignItems: "end" }}>
          <div>
            <div style={{ color: "#64748b", fontSize: 10, fontWeight: 700, textTransform: "uppercase", letterSpacing: 0.6, marginBottom: 6 }}>PAN Number</div>
            <input data-testid="pan-input" value={pan} onChange={(e) => setPan(e.target.value.toUpperCase())} onKeyDown={(e) => e.key === "Enter" && submit()} maxLength={10} placeholder="ABCDE1234F" style={inputStyle} />
          </div>
          <div>
            <div style={{ color: "#64748b", fontSize: 10, fontWeight: 700, textTransform: "uppercase", letterSpacing: 0.6, marginBottom: 6 }}>Expected Type (optional)</div>
            <select data-testid="pan-expected-type" value={expectedType} onChange={(e) => setExpectedType(e.target.value)} style={{ ...inputStyle, fontFamily: "inherit", letterSpacing: 0 }}>
              <option value="">— Any —</option>
              <option value="Individual">Individual</option>
              <option value="HUF">HUF</option>
              <option value="Company">Company</option>
              <option value="Firm">Firm</option>
              <option value="Trust">Trust</option>
            </select>
          </div>
          <button data-testid="pan-verify-btn" onClick={submit} disabled={loading} style={btnPrimary}>
            {loading ? <Loader2 className="animate-spin" size={14} /> : <Search size={14} />}
            {loading ? "Verifying..." : "Verify PAN"}
          </button>
        </div>
      </div>

      {/* Result */}
      {result && (
        <>
        <div style={{ marginBottom: 16 }}>
          <IdentityResultCard person={fromPanVerify(result, pan)} />
        </div>
        <div data-testid="pan-result-card" style={{ background: "#0d1117", border: `1px solid ${riskColor}`, borderRadius: 12, padding: 22, marginBottom: 16 }}>
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center gap-3">
              <ShieldCheck style={{ width: 22, height: 22, color: "#2563eb" }} />
              <div>
                <div style={{ color: "#f1f5f9", fontSize: 18, fontWeight: 700, fontFamily: "ui-monospace, monospace", letterSpacing: 1 }}>{result.pan_number}</div>
                <div style={{ color: "#94a3b8", fontSize: 11 }}>{result.name_on_pan || result.verification_result?.name_on_pan || "Name masked"}</div>
              </div>
              <span data-testid="pan-status-badge" style={{ background: `${statusColor}22`, color: statusColor, border: `1px solid ${statusColor}55`, fontSize: 10, padding: "3px 10px", borderRadius: 4, fontWeight: 700, letterSpacing: 0.4, textTransform: "uppercase", display: "inline-flex", alignItems: "center", gap: 5 }}>
                {status === "VALID" ? <CheckCircle2 size={11} /> : status === "FORMAT_ERROR" ? <AlertTriangle size={11} /> : <XCircle size={11} />} {status}
              </span>
              <span data-testid="pan-risk-badge" style={{ background: `${riskColor}22`, color: riskColor, border: `1px solid ${riskColor}55`, fontSize: 10, padding: "3px 10px", borderRadius: 4, fontWeight: 700, letterSpacing: 0.4, textTransform: "uppercase" }}>{riskLevel === "FORMAT_WARNING" ? "WARNING" : `${riskLevel} risk`}</span>
            </div>
            <div className="flex items-center gap-2">
              <button onClick={() => { setResult(null); setSanctionsResult(null); }} style={btnGhost}><X size={11} /> Clear</button>
            </div>
          </div>

          {/* Format error guidance — WARNING (not critical) */}
          {isFormatError && formatGuidance?.show_if_format_error && (
            <div data-testid="pan-format-warning" style={{ background: "rgba(245,158,11,0.08)", border: "1px solid #f59e0b55", borderRadius: 6, padding: 14, marginBottom: 14 }}>
              <div style={{ display: "flex", gap: 10, alignItems: "flex-start" }}>
                <AlertTriangle size={14} style={{ color: "#f59e0b", marginTop: 2, flexShrink: 0 }} />
                <div style={{ flex: 1 }}>
                  <div style={{ color: "#fbbf24", fontSize: 13, fontWeight: 700, marginBottom: 6 }}>{formatGuidance.message}</div>
                  <div style={{ color: "#cbd5e1", fontSize: 12, lineHeight: 1.7 }}>
                    • <strong>Valid format:</strong> {formatGuidance.valid_format}<br />
                    • <strong>4th character:</strong> {formatGuidance["4th_char_guide"]}<br />
                    • <span style={{ color: "#94a3b8" }}>{formatGuidance.user_message}</span>
                  </div>
                  <div style={{ color: "#94a3b8", fontSize: 11, marginTop: 8, fontStyle: "italic" }}>
                    This is a format warning (likely typo) — not a fraud signal.
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Compliance note */}
          {result.summary?.compliance_note && !isFormatError && (
            <div style={{ background: "rgba(37,99,235,0.06)", border: "1px solid #2563eb55", borderRadius: 6, padding: 12, marginBottom: 14, display: "flex", gap: 8, alignItems: "flex-start" }}>
              <Sparkles size={12} style={{ color: "#3b82f6", marginTop: 2, flexShrink: 0 }} />
              <div style={{ color: "#cbd5e1", fontSize: 12, lineHeight: 1.5 }}>{result.summary.compliance_note}</div>
            </div>
          )}

          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(160px, 1fr))", gap: 14, background: "#080c12", padding: 14, borderRadius: 8, marginBottom: 14 }}>
            <KV k="PAN Type" v={panType || "—"} sub={isCompany ? "Non-individual" : null} />
            <KV
              k="Aadhaar Linked"
              v={aadhaarValue}
              color={aadhaarColor}
              sub={aadhaar?.not_applicable_reason}
            />
            <KV
              k="Deceased"
              v={deceasedValue}
              color={deceasedColor}
              sub={deceased?.not_applicable_reason}
            />
            <KV k="Source" v={result.source || "INCOME_TAX_DEPT_FREE"} mono />
            <KV k="Cost" v={result.cost || "FREE"} color="#22c55e" />
          </div>

          {/* PAN structure breakdown */}
          {result.format_validation?.pan_structure && !isFormatError && (
            <div style={{ background: "#080c12", border: "1px solid #1e2530", borderRadius: 8, padding: 12, marginBottom: 14 }}>
              <div style={{ color: "#64748b", fontSize: 10, fontWeight: 700, textTransform: "uppercase", letterSpacing: 0.6, marginBottom: 8 }}>PAN Structure Decoded</div>
              <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(110px, 1fr))", gap: 10, fontSize: 12 }}>
                <KV k="Issuing Code" v={result.format_validation.pan_structure.issuing_code} mono />
                <KV k="Type Char" v={result.format_validation.pan_structure.taxpayer_type_char} mono />
                <KV k="Name Initial" v={result.format_validation.pan_structure.name_initial} mono />
                <KV k="Serial" v={result.format_validation.pan_structure.serial_number} mono />
                <KV k="Check Digit" v={result.format_validation.pan_structure.check_digit} mono />
              </div>
            </div>
          )}

          {/* Risk flags */}
          {result.risk_assessment?.risk_flags?.length > 0 && (
            <div style={{ marginBottom: 14 }}>
              <div style={{ color: "#64748b", fontSize: 10, fontWeight: 700, textTransform: "uppercase", letterSpacing: 0.6, marginBottom: 8 }}>Risk Flags</div>
              <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
                {result.risk_assessment.risk_flags.map((f, i) => {
                  const sev = (f.severity || "MEDIUM").toUpperCase();
                  const c = sev === "WARNING" ? "#f59e0b" : (RISK_COLOR[sev] || "#f59e0b");
                  return (
                    <div key={i} data-testid={`pan-risk-flag-${i}`} style={{ background: `${c}10`, border: `1px solid ${c}44`, borderRadius: 6, padding: "8px 12px" }}>
                      <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 2 }}>
                        <AlertTriangle size={12} style={{ color: c }} />
                        <span style={{ color: c, fontSize: 12, fontWeight: 700 }}>{f.display_text || f.label || f.type}</span>
                        <span style={{ marginLeft: "auto", color: c, fontSize: 9, fontWeight: 700, textTransform: "uppercase" }}>{sev}</span>
                      </div>
                      {f.detail && <div style={{ color: "#cbd5e1", fontSize: 11, marginLeft: 20 }}>{f.detail}</div>}
                      {f.action && <div style={{ color: "#94a3b8", fontSize: 10, marginLeft: 20, marginTop: 2 }}>→ {f.action}</div>}
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          {/* Action buttons — Run MCA21 / Run Sanctions */}
          {!isFormatError && (
            <div data-testid="pan-next-actions" style={{ background: "#080c12", border: "1px solid #1e2530", borderRadius: 8, padding: 14, marginBottom: 14 }}>
              <div style={{ color: "#64748b", fontSize: 10, fontWeight: 700, textTransform: "uppercase", letterSpacing: 0.6, marginBottom: 10 }}>Next Steps · Rudrik AML Suite</div>
              <div style={{ display: "flex", flexWrap: "wrap", gap: 8 }}>
                {isCompany && (
                  <button
                    data-testid="pan-run-mca21-btn"
                    onClick={() => navigate("/mca21")}
                    style={btnAction}
                  >
                    <Building2 size={13} /> Run MCA21 Lookup <ArrowRight size={12} />
                  </button>
                )}
                {!isCompany && status === "VALID" && (
                  <button
                    data-testid="pan-run-sanctions-btn"
                    onClick={() => {
                      const name = result.name_on_pan || result.verification_result?.name_on_pan;
                      if (!name) { toast.error("Name not available from IT Dept response"); return; }
                      runSanctions(name);
                    }}
                    disabled={sanctionsLoading}
                    style={btnAction}
                  >
                    {sanctionsLoading ? <Loader2 className="animate-spin" size={13} /> : <ShieldAlert size={13} />}
                    Run Sanctions Check <ArrowRight size={12} />
                  </button>
                )}
                {stepsToShow.slice(0, 4).map((step, idx) => (
                  <div key={idx} style={{ background: "rgba(124,58,237,0.08)", border: "1px solid #7c3aed44", borderRadius: 6, padding: "6px 10px", fontSize: 11, color: "#c4b5fd", display: "inline-flex", alignItems: "center", gap: 6 }}>
                    <Info size={10} />
                    <span style={{ fontWeight: 600 }}>{step.action}</span>
                    {step.urgency && <span style={{ background: step.urgency === "MANDATORY" ? "#dc262633" : "#f59e0b33", color: step.urgency === "MANDATORY" ? "#fca5a5" : "#fcd34d", fontSize: 9, padding: "1px 5px", borderRadius: 3, fontWeight: 700 }}>{step.urgency}</span>}
                  </div>
                ))}
              </div>
              {sanctionsLoading && (
                <div style={{ marginTop: 10, color: "#94a3b8", fontSize: 11, display: "flex", alignItems: "center", gap: 6 }}>
                  <Loader2 className="animate-spin" size={11} /> Auto-screening against 200+ watchlists…
                </div>
              )}
            </div>
          )}

          {/* Auto sanctions result */}
          {sanctionsResult && (
            <div data-testid="pan-sanctions-result" style={{ background: "#080c12", border: `1px solid ${sanctionsResult.result?.total_matches > 0 ? "#dc262655" : "#22c55e55"}`, borderRadius: 8, padding: 12, marginBottom: 14 }}>
              <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 6 }}>
                <span style={{ color: "#94a3b8", fontSize: 10, fontWeight: 700, textTransform: "uppercase", letterSpacing: 0.6 }}>Auto Sanctions Screening</span>
                <span style={{ color: sanctionsResult.result?.total_matches > 0 ? "#fca5a5" : "#86efac", fontSize: 11, fontWeight: 700 }}>
                  {sanctionsResult.result?.total_matches > 0 ? `${sanctionsResult.result.total_matches} MATCH(ES)` : "CLEAR"}
                </span>
              </div>
              <div style={{ color: "#cbd5e1", fontSize: 12 }}>
                {sanctionsResult.result?.total_matches > 0
                  ? `${sanctionsResult.result.matches?.[0]?.name || "Match"} — ${(sanctionsResult.risk?.severity_level || "").toUpperCase()}`
                  : "No matches on OFAC, UN, EU, SEBI, NSE, RBI, MHA UAPA."}
              </div>
              <button onClick={() => navigate("/opensanctions")} style={{ ...btnGhost, marginTop: 8 }}>
                Open full Sanctions panel <ArrowRight size={11} />
              </button>
            </div>
          )}

          {/* Certificate */}
          {result.certificate && !isFormatError && (
            <div style={{ background: "#080c12", border: "1px solid #1e2530", borderRadius: 8, padding: 12, display: "flex", gap: 10, alignItems: "flex-start" }}>
              <Award size={14} style={{ color: "#22c55e", marginTop: 2, flexShrink: 0 }} />
              <div style={{ flex: 1 }}>
                <div style={{ color: "#22c55e", fontSize: 10, fontWeight: 700, textTransform: "uppercase", letterSpacing: 0.6, marginBottom: 4 }}>Verification Certificate · {result.certificate.certificate_number}</div>
                <div style={{ color: "#cbd5e1", fontSize: 11, lineHeight: 1.5 }}>{result.certificate.statement}</div>
                <div style={{ color: "#64748b", fontSize: 10, marginTop: 4 }}>{result.certificate.powered_by}</div>
              </div>
            </div>
          )}
        </div>
        </>
      )}

      {/* SESSION-ONLY: no recent lookups table (data not persisted) */}
    </div>
  );
}

function Th({ children }) { return <th style={{ textAlign: "left", padding: "10px 12px", color: "#64748b", fontSize: 10, fontWeight: 700, textTransform: "uppercase", letterSpacing: 0.5 }}>{children}</th>; }
function Td({ children }) { return <td style={{ padding: "10px 12px", verticalAlign: "middle" }}>{children}</td>; }
